import React, { useState, useMemo } from "react";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { List } from "react-window";
import { ChevronDown, ChevronRight, GitCommit, Maximize2 } from "lucide-react";

/**
 * Parse unified diff format (@@ hunks with +/- prefixes).
 */
const parseUnifiedDiffLines = (diffString) => {
    const result = [];
    let newLineNum = 1;
    let oldLineNum = 1;

    for (const line of diffString.split("\n")) {
        if (line.startsWith("@@")) {
            // Parse hunk header to get starting line numbers
            const match = line.match(/@@ -(\d+),?\d* \+(\d+),?\d* @@/);
            if (match) {
                oldLineNum = parseInt(match[1], 10);
                newLineNum = parseInt(match[2], 10);
            }
            result.push({ type: "hunk-header", content: line, lineNumber: null });
        } else if (line.startsWith("+")) {
            result.push({ type: "addition", content: line.substring(1), lineNumber: newLineNum });
            newLineNum++;
        } else if (line.startsWith("-")) {
            result.push({ type: "deletion", content: line.substring(1), lineNumber: oldLineNum });
            oldLineNum++;
        } else if (line.startsWith(" ")) {
            result.push({ type: "context", content: line.substring(1), lineNumber: newLineNum });
            oldLineNum++;
            newLineNum++;
        }
    }

    return result.filter((l) => l.type === "hunk-header" || l.content !== "");
};

/**
 * Parse legacy diff format (++ /-- /   prefixes with line numbers).
 */
const parseLegacyDiffLines = (diffString) => {
    return diffString
        .split("\n")
        .map((line, index) => {
            if (line.startsWith("++ ")) {
                return { type: "addition", content: line.substring(3), lineNumber: index + 1 };
            } else if (line.startsWith("-- ")) {
                return { type: "deletion", content: line.substring(3), lineNumber: index + 1 };
            } else if (line.startsWith("  ")) {
                return { type: "context", content: line.substring(2), lineNumber: index + 1 };
            }
            return { type: "unknown", content: line, lineNumber: index + 1 };
        })
        .filter((line) => line.content !== "");
};

/**
 * Auto-detect format and parse accordingly.
 * Unified format starts with @@ or +/- lines; legacy uses ++ /-- .
 */
const parseDiffLines = (diffString) => {
    if (!diffString) return [];

    // Check if any line starts with @@ → unified format
    const lines = diffString.split("\n");
    const isUnified = lines.some((l) => l.startsWith("@@"));

    if (isUnified) return parseUnifiedDiffLines(diffString);
    return parseLegacyDiffLines(diffString);
};

// Virtualized row component for diff lines
const DiffRow = ({ index, style, lines, getChangeIcon, getChangeColor }) => {
    const line = lines[index];

    if (line.type === "hunk-header") {
        return (
            <div
                style={{ ...style, padding: "4px 12px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f0f4f8" }}
            >
                <div className="text-xs font-mono text-gray-500">{line.content}</div>
            </div>
        );
    }

    return (
        <div
            style={{
                ...style,
                padding: "8px 12px",
                borderBottom: "1px solid #f3f4f6",
                backgroundColor: getChangeColor(line.type).includes("green")
                    ? "#f0fdf4"
                    : getChangeColor(line.type).includes("red")
                    ? "#fef2f2"
                    : "#f9fafb",
            }}
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="flex-shrink-0 w-8 text-center text-xs text-gray-500">
                    {line.lineNumber}
                </div>
                <div className="flex-shrink-0 w-6 text-center">
                    {getChangeIcon(line.type)}
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className={`text-xs font-mono whitespace-pre overflow-x-auto ${
                            line.type === "addition"
                                ? "text-green-600"
                                : line.type === "deletion"
                                ? "text-red-600"
                                : "text-gray-600"
                        }`}
                        style={{ lineHeight: "1.4", maxWidth: "100%" }}
                    >
                        {line.content || " "}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DiffDisplay = ({ diff, onOpenModal }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const diffLines = useMemo(() => {
        return diff ? parseDiffLines(diff) : [];
    }, [diff]);

    const hasChanges = diffLines.some(
        (line) => line.type === "addition" || line.type === "deletion"
    );

    if (!diff) return null;

    const getChangeIcon = (type) => {
        switch (type) {
            case "addition":
                return <span className="text-green-600 font-bold">+</span>;
            case "deletion":
                return <span className="text-red-600 font-bold">-</span>;
            case "context":
                return <span className="text-gray-400 font-bold">&middot;</span>;
            default:
                return null;
        }
    };

    const getChangeColor = (type) => {
        switch (type) {
            case "addition":
                return "bg-green-50 border-green-200";
            case "deletion":
                return "bg-red-50 border-red-200";
            case "context":
                return "bg-gray-50 border-gray-200";
            default:
                return "bg-gray-50 border-gray-200";
        }
    };

    const additionsCount =
        diff.additionsCount ||
        diffLines.filter((line) => line.type === "addition").length;
    const deletionsCount =
        diff.deletionsCount ||
        diffLines.filter((line) => line.type === "deletion").length;

    return (
        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <GitCommit className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                            Your code changes
                        </span>

                        {additionsCount > 0 && (
                            <Badge
                                variant="outline"
                                className="text-xs text-green-600 border-green-300"
                            >
                                +{additionsCount}
                            </Badge>
                        )}
                        {deletionsCount > 0 && (
                            <Badge
                                variant="outline"
                                className="text-xs text-red-600 border-red-300"
                            >
                                -{deletionsCount}
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {hasChanges && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="h-6 w-6 p-0"
                            >
                                {isExpanded ? (
                                    <ChevronDown className="h-3 w-3" />
                                ) : (
                                    <ChevronRight className="h-3 w-3" />
                                )}
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onOpenModal(diff)}
                            className="h-6 w-6 p-0"
                            title="View full screen"
                        >
                            <Maximize2 className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </div>

            {isExpanded && diffLines.length > 0 && (
                <div className="bg-white border border-gray-100">
                    <List
                        height={320}
                        rowCount={diffLines.length}
                        rowHeight={32}
                        rowComponent={DiffRow}
                        rowProps={{
                            lines: diffLines,
                            getChangeIcon,
                            getChangeColor,
                        }}
                        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                    />
                </div>
            )}
        </div>
    );
};

export default DiffDisplay;
export { parseDiffLines };
